import { useCurrent } from "./useCurrent";
import { useEffect, useReducer } from "react";
const INITIAL_STATE = {
    data: null,
    error: null,
    status: 'initial',
};
const INITIAL_LOADING_STATE = Object.assign(Object.assign({}, INITIAL_STATE), { status: 'loading' });
function subscriptionReducer(state, action) {
    switch (action.type) {
        case 'loading':
            return Object.assign(Object.assign({}, INITIAL_STATE), { data: null, status: action.type });
        case 'error':
            return Object.assign(Object.assign({}, state), { status: action.type, error: action.data });
        case 'data':
            return { error: null, status: 'loaded', data: action.data };
    }
}
function makeConfig(config) {
    return Object.assign({ immediate: true, map: value => value }, (typeof config === 'function' ? { fn: config } : config));
}
/**
 * @example
 * // Most basic usage, watch a med
 * const {data: med, loading} = useSubscription(
 *    (onData, ce) => med_model.watch_med(mid, onData, {ce}),
 *    [mid]
 * );
 * @example
 * // Conditional watch, optional mapping
 * const {data: med, loading} = useSubscription({
 *   immediate: !!mid,
 *   fn: (onData, ce) => med_model.watch_med(mid, onData, {ce}),
 *   map: ({ hist, ...med }) => med // omit the history field
 * }, [mid]);
 */
export function useSubscription(config, deps = []) {
    const cfg = makeConfig(config);
    const cfgRef = useCurrent(cfg);
    const initial = cfg.immediate ? INITIAL_LOADING_STATE : INITIAL_STATE;
    const [state, dispatch] = useReducer(subscriptionReducer, initial);
    useEffect(() => {
        dispatch({ type: 'loading' });
        if (!cfgRef.current.immediate)
            return;
        const onData = (data) => dispatch({ type: 'data', data: cfgRef.current.map(data) });
        const onError = (error) => dispatch({ type: 'error', data: error });
        const unsub = cfgRef.current.fn(onData, onError);
        return () => {
            unsub();
        };
    }, [cfg.immediate, ...deps]);
    return Object.assign(Object.assign({}, state), { get loading() {
            return state.status === 'loading';
        } });
}
